import { Icon, IconButton, InputBase, Popover } from "@mui/material";
import { useEffect, useRef, useState } from "react";

import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";

function WhatsAppMessageComposer({ conversation, sendMessage }) {
  const [message, setMessage] = useState("");
  const [media, setMedia] = useState([]);
  const [emojiCategories, setEmojiCategories] = useState([]);
  const [emojiAnchor, setEmojiAnchor] = useState(null);
  const fileInputRef = useRef(null);
  const composerRef = useRef(null);

  useEffect(() => {
    fetch("/assets/emojis.json")
      .then((response) => response.json())
      .then((data) => {
        setEmojiCategories(data);
      })
      .catch(() => {
        setEmojiCategories([]);
      });
  }, []);

  const handleSend = () => {
    if (!message.trim() && media.length === 0) {
      return;
    }

    sendMessage({
      content_type: media.length > 0 ? "media" : "text",
      content: message.trim(),
      media: media,
    });

    setMessage("");
    setMedia([]);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const handleMediaChange = (event) => {
    const files = Array.from(event.target.files || []);

    if (files.length > 0) {
      setMedia(files);
    }

    event.target.value = "";
  };

  const openMediaPicker = () => {
    if (!conversation) {
      return;
    }

    fileInputRef.current?.click();
  };

  const openEmojiPicker = (event) => {
    if (!conversation) {
      return;
    }

    setEmojiAnchor(event.currentTarget);
  };

  const closeEmojiPicker = () => {
    setEmojiAnchor(null);
  };

  const addEmoji = (emoji) => {
    setMessage((currentMessage) => currentMessage + emoji);
  };

  return (
    <MDBox
      ref={composerRef}
      sx={{
        width: "100%",
        borderTop: "1px solid #e0e0e0",
        backgroundColor: "#f0f2f5",
        px: 2,
        py: 1,
      }}
    >
      <input
        ref={fileInputRef}
        type="file"
        hidden
        accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
        onChange={handleMediaChange}
      />

      {media.length > 0 && (
        <MDBox
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 1,
            px: 1,
          }}
        >
          <MDTypography
            variant="caption"
            sx={{
              flex: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {media.length} file{media.length > 1 ? "s" : ""} selected
          </MDTypography>

          <IconButton size="small" onClick={() => setMedia([])}>
            <Icon fontSize="small">close</Icon>
          </IconButton>
        </MDBox>
      )}

      <Popover
        open={Boolean(emojiAnchor)}
        anchorEl={emojiAnchor}
        onClose={closeEmojiPicker}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            sx: {
              width: composerRef.current?.offsetWidth || 360,
              maxWidth: composerRef.current?.offsetWidth || 360,
              height: 420,
              maxHeight: "calc(100vh - 32px)",
              p: 1,
              overflow: "hidden",
              backgroundColor: "#ffffff",
              opacity: 1,
            },
          },
        }}
      >
        <MDBox
          sx={{
            height: "100%",
            overflowY: "auto",
          }}
        >
          {emojiCategories.map((category) => (
            <MDBox
              key={category.category}
              sx={{
                mb: 1.5,
              }}
            >
              <MDTypography
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#667781",
                  px: 1,
                  py: 0.5,
                }}
              >
                {category.category}
              </MDTypography>

              <MDBox
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(12, 1fr)",
                }}
              >
                {category.emojis.map((item) => (
                  <button
                    key={`${category.category}-${item.title}-${item.emoji}`}
                    type="button"
                    title={item.title}
                    onClick={() => addEmoji(item.emoji)}
                    style={{
                      width: "40px",
                      height: "40px",
                      padding: 0,
                      margin: 0,
                      border: "none",
                      borderRadius: "4px",
                      background: "transparent",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      opacity: 1,
                      filter: "none",
                    }}
                    onMouseEnter={(event) => {
                      event.currentTarget.style.backgroundColor = "#f0f2f5";
                    }}
                    onMouseLeave={(event) => {
                      event.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    <span
                      style={{
                        display: "block",
                        fontFamily:
                          '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif',
                        fontSize: "22px",
                        lineHeight: "22px",
                        fontWeight: "normal",
                        opacity: 1,
                        filter: "none",
                        color: "initial",
                        textShadow: "none",
                        WebkitTextFillColor: "initial",
                      }}
                    >
                      {item.emoji}
                    </span>
                  </button>
                ))}
              </MDBox>
            </MDBox>
          ))}
        </MDBox>
      </Popover>

      <MDBox
        sx={{
          display: "flex",
          alignItems: "flex-end",
          width: "100%",
          backgroundColor: "#ffffff",
          borderRadius: "24px",
          px: 1,
          py: 0.5,
        }}
      >
        <IconButton
          size="small"
          onClick={openEmojiPicker}
          disabled={!conversation}
          sx={{
            mr: 0.5,
          }}
        >
          <Icon>emoji_emotions</Icon>
        </IconButton>

        <IconButton
          size="small"
          onClick={openMediaPicker}
          disabled={!conversation}
          sx={{
            mr: 0.5,
          }}
        >
          <Icon>attach_file</Icon>
        </IconButton>

        <InputBase
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          onKeyDown={handleKeyDown}
          disabled={!conversation}
          multiline
          maxRows={5}
          placeholder={conversation ? "Type a message" : "Select a conversation"}
          sx={{
            flex: 1,
            fontSize: "14px",
            px: 1,
            py: 0.75,
            lineHeight: 1.5,
          }}
        />

        <IconButton
          size="small"
          onClick={handleSend}
          disabled={!conversation || (!message.trim() && media.length === 0)}
          sx={{
            ml: 0.5,
          }}
        >
          <Icon>send</Icon>
        </IconButton>
      </MDBox>
    </MDBox>
  );
}

export default WhatsAppMessageComposer;
